/**
 * Compact format of the pre-computed land-use label tiles (public/data/labels, written by
 * scripts/build-labels.mjs, read by labels.js):
 *   <z>/<x>/<y>.json  { z, x, y, n: [names], L: [[id12, layer, nameIdx, lng, lat, r, area|null], …] }
 *   manifest.json     { v, generated, source, grids: { "<z>": { x0, y0, w, h, built, have } } }
 * `built` / `have` are base64 bitsets over the grid (row-major from x0, y0): tile computed / tile
 * has labels. A tile that is not "built" (or outside the grid) is computed live by the engine.
 */

/** Short, stable cell id: 12 hex digits of the official UUID (unique within a tile). */
export const shortId = (id) => String(id).replace(/-/g, '').slice(0, 12);

/** Engine result { z, x, y, labels: [{ id, l, n, p, r, a }] } -> compact tile. */
export function encodeLabelTile(r) {
  const names = [], index = new Map(), L = [];
  for (const l of r.labels) {
    let k = index.get(l.n);
    if (k === undefined) { k = names.length; names.push(l.n); index.set(l.n, k); }
    L.push([shortId(l.id), l.l, k, l.p[0], l.p[1], l.r, l.a == null ? null : l.a]);
  }
  return { z: r.z, x: r.x, y: r.y, n: names, L };
}

/** Compact tile -> [{ id, l, n, p, r, a }] (the engine's fields, id shortened). */
export function decodeLabelTile(d) {
  const names = d?.n || [];
  return (d?.L || []).map((r) => ({ id: r[0], l: r[1], n: names[r[2]], p: [r[3], r[4]], r: r[5], a: r[6] }));
}

const fromB64 = (s) => (typeof atob === 'function'
  ? Uint8Array.from(atob(s), (c) => c.charCodeAt(0))
  : new Uint8Array(Buffer.from(s, 'base64')));

/** Manifest JSON -> { v, generated, grids: { z: { x0, y0, w, h, built: Uint8Array, have: Uint8Array } } } */
export function parseLabelManifest(m) {
  if (!m || !m.grids) return null;
  const grids = {};
  for (const [z, g] of Object.entries(m.grids)) grids[z] = { x0: g.x0, y0: g.y0, w: g.w, h: g.h, built: fromB64(g.built), have: fromB64(g.have) };
  return { v: m.v, generated: m.generated || null, grids };
}

/** 'have' | 'empty' for a pre-computed tile, null when it must be computed live. */
export function labelTileState(man, z, x, y) {
  const g = man?.grids?.[z];
  if (!g) return null;
  const cx = x - g.x0, cy = y - g.y0;
  if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return null;
  const i = cy * g.w + cx, m = 1 << (i & 7);
  if (!(g.built[i >> 3] & m)) return null;
  return g.have[i >> 3] & m ? 'have' : 'empty';
}
